import * as THREE from 'three';
import EventBus from './EventBus.js';

// 场景管理器
export default class SceneManager {

    static instance = null;

    constructor(){
        
        // 存储所有场景
        this.scenes = new Map();
        // 当前激活的场景
        this.activeScene = null;
        // 全局灯光（可以在所有场景中共享）
        this.globalLights = [];
        // 全局雾效
        this.fog = null;
    }

    /**
     * 创建并添加一个新场景
     * @param {string} sceneName - 场景名称
     * @param {object} options - 场景选项（背景颜色、雾效等）
     * @returns {THREE.Scene} - 创建的场景
     */
    createScene(sceneName, options = {}) {
        if (this.scenes.has(sceneName)) {
            return this.scenes.get(sceneName);
        }

        const scene = new THREE.Scene();

        // 设置背景颜色
        if (options.backgroundColor) {
            scene.background = new THREE.Color(options.backgroundColor);
        }

        // 设置雾效
        if (options.fog) {
            this.setFog(sceneName, options.fog);
        }

        this.scenes.set(sceneName, scene);

        // 如果是第一个场景，自动激活
        if (!this.activeScene) {
            this.switchScene(sceneName);
        }

        if(options.arrow){
            // 添加xyz方向箭头
            this.addArrow(scene);
        }

        if(options.gridHelper){
            // 添加网络线
            const gridHelper = new THREE.GridHelper(100, 100,
                0x666666,   // 中心轴线颜色（红色）
                0xcccccc    // 普通网格线颜色（绿色）
                ); // 10x10 网格
            scene.add(gridHelper); 
        }
        return scene;
    }

    addArrow(scene){
        scene.add(new THREE.AxesHelper(2));

        const directionx = new THREE.Vector3(1, 0, 0); // 箭头方向（这里是 X 轴正方向）
        const directiony = new THREE.Vector3(0, 1, 0); // 箭头方向（这里是 Y 轴正方向）
        const directionz = new THREE.Vector3(0, 0, 1); // 箭头方向（这里是 Z 轴正方向）
        const origin = new THREE.Vector3(0, 0, 0);    // 箭头起点（这里是世界坐标系原点）
        const colorx = 0xff0000;                       // 箭头颜色（红色）
        const colory = 0x00ff00;                       // 箭头颜色（绿色）
        const colorz = 0x0000ff;                       // 箭头颜色（蓝色）
        const headLength = 0.3;                         // 箭头头部长度（默认是总长度的 1/5）
        const headWidth = 0.1;                        // 箭头头部宽度（默认是头部长度的 2/3）
        const length = 3;                             // 箭头总长度

        // 1. 添加X轴
        scene.add(new THREE.ArrowHelper(
            directionx, origin, length, colorx, headLength, headWidth 
        ));
    
        // 2. 添加Y轴
        scene.add(new THREE.ArrowHelper(
            directiony, origin, length, colory, headLength, headWidth 
        ));
        
        // 3. 添加z轴
        scene.add(new THREE.ArrowHelper(
            directionz, origin, length, colorz, headLength, headWidth 
        ));
    }

    /**
     * 切换到指定场景
     * @param {string} sceneName - 要切换到的场景名称
     */
    switchScene(sceneName) {
        if (!this.scenes.has(sceneName)) {
            console.error(`场景 "${sceneName}" 不存在`);
            return;
        }

        const newScene = this.scenes.get(sceneName);

        // 如果当前有激活的场景，先从渲染循环中移除
        if (this.activeScene) {
            EventBus.emit('sceneDeactivated', this.activeScene);
        }

        this.activeScene = newScene;

        // 通知渲染器切换场景
        EventBus.emit('sceneSwitched', newScene);
    }

    /**
     * 获取当前激活的场景
     * @returns {THREE.Scene|null}
     */
    getActiveScene() {
        return this.activeScene;
    }

    /**
     * 获取指定场景
     * @param {string} sceneName - 场景名称
     * @returns {THREE.Scene|null}
     */
    getScene(sceneName) {
        return this.scenes.get(sceneName);
    }

    /**
     * 为指定场景设置雾效
     * @param {string} sceneName - 场景名称
     * @param {object} fogOptions - 雾效选项
     * @param {string} fogOptions.type - 雾效类型 ('linear' 或 'exponential')
     * @param {number} fogOptions.near - 近裁剪面
     * @param {number} fogOptions.far - 远裁剪面
     * @param {number} fogOptions.density - 密度（仅用于指数雾）
     * @param {number} fogOptions.color - 雾的颜色
     */
    setFog(sceneName, fogOptions) {
        const scene = this.scenes.get(sceneName);
        if (!scene) return;

        let fog;
        const color = new THREE.Color(fogOptions.color || 0xffffff);

        switch (fogOptions.type) {
            case 'linear':
                fog = new THREE.Fog(color, fogOptions.near, fogOptions.far);
                break;
            case 'exponential':
                fog = new THREE.FogExp2(color, fogOptions.density || 0.01);
                break;
            default:
                console.warn(`不支持的雾效类型: ${fogOptions.type}`);
                return;
        }

        scene.fog = fog;
        this.fog = fog; // 保存全局雾效设置
    }

    /**
     * 添加全局灯光配置
     * 这会为所有已存在的场景创建并添加新的灯光实例
     * @param {object} lightConfig - 灯光的配置对象
     */
    addGlobalLightConfig(lightConfig) {
        // 1. 保存灯光配置
        this.globalLightConfigs.push(lightConfig);
    
        // 2. 为每一个已存在的场景创建并添加灯光实例
        this.scenes.forEach(scene => {
            const light = this.createLightFromConfig(lightConfig);
            if (light) {
                scene.add(light);
            }
        });
    }

    /**
     * 根据配置对象创建灯光实例
     * @param {object} config - 灯光配置
     * @returns {THREE.Light|null} - 创建的灯光实例
     */
    createLightFromConfig(config) {
        let light;
        switch (config.type) {
        case 'AmbientLight':
            light = new THREE.AmbientLight(config.color, config.intensity);
            break;
        case 'DirectionalLight':
            light = new THREE.DirectionalLight(config.color, config.intensity);
            if (config.position) {
                light.position.set(config.position.x, config.position.y, config.position.z);
            }
            if (config.castShadow !== undefined) {
                light.castShadow = config.castShadow;
            }
            // 可以根据需要添加更多属性
            break;
        // 可以扩展其他灯光类型
        default:
            console.warn(`不支持的灯光类型: ${config.type}`);
            return null;
        }
        return light;
    }

    /**
     * 从所有场景中移除全局灯光
     * @param {THREE.Light} light - 灯光对象
     */
    removeGlobalLight(light) {
        const index = this.globalLights.indexOf(light);
        if (index !== -1) {
            this.globalLights.splice(index, 1);

            // 从所有场景中移除
            this.scenes.forEach(scene => {
                scene.remove(light);
            });
        }
    }

    /**
     * 清空指定场景中的所有对象
     * @param {string} sceneName - 场景名称
     */
    clearScene(sceneName) {
        const scene = this.scenes.get(sceneName);
        if (!scene) return;

        // 移除场景中的所有对象（保留全局灯光）
        scene.traverse(child => {
            if (!this.globalLights.includes(child)) {

                if (child.geometry) child.geometry.dispose();

                if (child.material) {
                    if (Array.isArray(child.material)) {
                        child.material.forEach(material => material.dispose());
                    } else {
                        child.material.dispose();
                    }
                }
                scene.remove(child);
            }
        });
    }

    /**
     * 销毁指定场景
     * @param {string} sceneName - 场景名称
     */
    destroyScene(sceneName) {
        const scene = this.scenes.get(sceneName);
        if (!scene) return;

        // 清空场景
        this.clearScene(sceneName);

        // 从场景映射中移除
        this.scenes.delete(sceneName);

        // 如果销毁的是当前激活的场景，切换到第一个可用场景
        if (this.activeScene === scene && this.scenes.size > 0) {
            const firstSceneName = Array.from(this.scenes.keys())[0];
            this.switchScene(firstSceneName);
        } else if (this.scenes.size === 0) {
            this.activeScene = null;
        }
    }

    /**
     * 销毁所有场景和资源
     */
    dispose() {
        this.scenes.forEach((scene, sceneName) => {
            this.destroyScene(sceneName);
        });
        this.globalLights = [];
        this.fog = null;
        this.instance = null;
    }

    /**
     * 获取单例实例
     * @returns {SceneManager}
     */
    static getInstance() {
        if (!this.instance) {
            this.instance = new SceneManager();
        }
        return this.instance;
    }

}
